import { Grid, makeStyles } from '@material-ui/core'
import React, { useState } from 'react'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import { Button, Dialog, InputField, TileList, DialogActions, DialogContent, DialogTitle } from '@paintscout/ui'
import type { DialogProps } from '@paintscout/ui'
import type { QuoteTemplateOption } from '@paintscout/util/builder'
import { getFeature, getObjectLabels, getQuoteTypeOptions, convertQuoteType } from '@paintscout/util/builder'
import { QuoteTypeSelect, useQuote } from '@paintscout/react-quote'
import find from 'lodash/find'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface QuoteTemplateTitleDialogProps extends DialogProps {
  onCancel: () => void
  onConfirm: (newLabel: string, newTypes: string[]) => void
  item: QuoteTemplateOption
  setItem: React.Dispatch<React.SetStateAction<QuoteTemplateOption>>
  options: OptionsDocument
  quote: QuoteDocument
}

export const useStyles = makeStyles((theme) => ({
  root: {},
  type: {
    marginTop: theme.typography.pxToRem(10)
  },
  dialogContent: {
    minHeight: 400
  }
}))

export default function QuoteTemplateTitleDialog(props: QuoteTemplateTitleDialogProps) {
  const { loading, onConfirm, onCancel, item, quote, options } = props
  const classes = useStyles(props)
  const { canDeactivateSettingsItems } = useFlags()
  const quoteTypeOptions = getQuoteTypeOptions({ options, canDeactivate: !!canDeactivateSettingsItems })
  const { updateQuote } = useQuote()
  const multiQuoteTemplates = getFeature({ options, path: 'quotes.multiQuoteTemplates' })
  const objectLabels = getObjectLabels({ options, invoice: quote?.is_invoice })
  const singeTypeSmartTemplate = item?.smartTemplate && !multiQuoteTemplates

  const [types, setTypes] = useState<string[]>(item.quoteTypes)
  const [label, setLabel] = useState<string>(item.label)
  const [selectedType, setSelectedType] = useState(find(quoteTypeOptions, { key: quote.int_ext.value }))
  const [disableConfirm, setDisableConfirm] = useState(false)

  const handleSetSelectedType = (key: string | string[]) => {
    if (Array.isArray(key)) {
      setSelectedType(null)
      setDisableConfirm(true)
    } else {
      const newType = find(quoteTypeOptions, { key })
      setDisableConfirm(false)
      setSelectedType(newType)
    }
  }

  return (
    <Dialog {...props}>
      <DialogTitle loading={loading}>Edit {objectLabels.quote.value} Template Label and Type</DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <form
          id="edit-template-title-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (item.smartTemplate && selectedType?.key && selectedType.key !== quote.int_ext?.value) {
              const typeConvertedQuote = convertQuoteType({
                quote,
                quoteType: selectedType,
                quoteTemplate: item,
                options
              })

              updateQuote({ quote: typeConvertedQuote, autosave: true })
            }
            onConfirm(label, types)
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputField
                label={'Template Label'}
                value={label}
                fullWidth
                onChange={(ev) => {
                  setLabel(ev.currentTarget.value)
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <QuoteTypeSelect
                classes={{ root: classes.type }}
                label="Type"
                allLabel={null}
                variant={singeTypeSmartTemplate ? 'single' : 'multi'}
                value={types}
                onChange={(value) => {
                  setTypes(value)
                  if (singeTypeSmartTemplate) {
                    handleSetSelectedType(value[0])
                  }
                }}
              />
            </Grid>
            {item?.smartTemplate && multiQuoteTemplates && (
              <Grid item xs={12}>
                <TileList
                  title={`${objectLabels.quote.value} Type`}
                  variant="select"
                  subtitle={`Rates, Line Items, and Areas are filtered by ${objectLabels.quote.value.toLowerCase()} type`}
                  items={quoteTypeOptions.filter((option) => types.includes(option.key))}
                  selected={selectedType?.key}
                  onSelect={(ev, key) => handleSetSelectedType(key)}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={onCancel} variant="textNoPadding" disabled={loading}>
            Cancel
          </Button>
        }
      >
        <Button disabled={loading || disableConfirm} type="submit" form="edit-template-title-form">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}
